import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { DroneReport, DroneReportFilters, DroneReportWithJobs } from "../models/drone-report.model";
import { firstValueFrom } from "rxjs";
import { HttpClient } from "@angular/common/http";  
import { Organization } from "../models";
import { ApiResponse } from "../models/api-response.model";

interface PaginatedReportsResponse {
  reports: DroneReport[];
  total: number;
  page: number;
  pageSize: number;
}

interface PaginatedApiResponse {
  status: number;
  data?: DroneReport[];
  total?: number;
  paging?: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages?: number;    
  };
  errors?: string[];
}

interface ApiResult<T> {
  status: number;
  data?: T;
  errors?: string[];
}

function isSuccess(response: { status: number }): boolean {
  return response.status >= 200 && response.status < 300;
}

@Injectable({
  providedIn: 'root'
})
export class DroneReportService {
    private readonly apiUrl = `${environment.apiUrl}/admin/drone-reports`;

    constructor(private http: HttpClient) {}

    // ======================
    // LISTING
    // ======================

    async listOrganizations(): Promise<any[]> {
        const response = await firstValueFrom(
            this.http.get<ApiResponse<{ organizations: Organization[]; total: number }>>(
                `${environment.apiUrl}/admin/organizations?pageSize=1000`
            )
        );
        if (response.success && response.data) {
            return response.data.organizations;
        }
        return [];
    }

    async listReports(filters: DroneReportFilters): Promise<PaginatedReportsResponse> {
        const params = new URLSearchParams();

        if (filters?.organizationId) {
            params.append('organizationId', filters.organizationId);
        }
        if (filters?.droneId && filters?.droneSource) {
            params.append('droneId', filters.droneId);
            params.append('droneSource', filters.droneSource);
        }
        if (filters?.status) {
            params.append('status', filters.status);
        }
        if (filters?.startDate) {
            params.append('startDate', filters.startDate);
        }
        if (filters?.endDate) {
            params.append('endDate', filters.endDate);
        }
        if (filters?.search) {
            params.append('search', filters.search);
        }
        if (filters?.page) {
            params.append('page', filters.page.toString());
        }
        if (filters?.pageSize) {
            params.append('limit', filters.pageSize.toString());
        }

        const queryString = params.toString();
        const url = queryString ? `${this.apiUrl}?${queryString}` : this.apiUrl;

        const response = await firstValueFrom(
            this.http.get<PaginatedApiResponse>(url)
        );

        if (isSuccess(response) && response.data) {
            return {
                reports: response.data,
                total: response.paging?.totalItems ?? 0,
                page: response.paging?.page ?? 1,
                pageSize: response.paging?.pageSize ?? 20,
            };
        } 

        return { reports: [], total: 0, page: 1, pageSize: 20 };
    }

    // ======================
    // REPORT GENERATION
    // ======================

    async generateReport(params: { droneId: string | undefined; droneSource: string; scope: string; startDate: string; endDate: string }): Promise<ApiResult<{ report: DroneReport; jobId: string }>>
    {
        return await firstValueFrom(
            this.http.post<ApiResult<{ report: DroneReport; jobId: string }>>(
                `${this.apiUrl}`,
                params
            )
        );
    }

    // ======================
    // REPORT DETAILS
    // ======================

    async getReport(id: string): Promise<DroneReportWithJobs | null> {
        const response = await firstValueFrom(
            this.http.get<ApiResult<{ report: DroneReport; jobs: DroneReportWithJobs['jobs'] }>>(`${this.apiUrl}/${id}`)
        );

        if (isSuccess(response) && response.data) {
            return {
            ...response.data.report,
            jobs: response.data.jobs || []
            };
        }

        return null;
    }

    // ======================
    // DELETE
    // ======================

    async deleteReport(id: string): Promise<boolean> {
        await firstValueFrom(
            this.http.delete<ApiResult<null>>(
                `${this.apiUrl}/${id}`
            )
        );
        return true;
    }

    // ======================
    // REGENERATE
    // ======================

    async regenerateReport(id: string): Promise<ApiResult<null>> {
        const result =await firstValueFrom(
            this.http.post<ApiResult<null>>(
                `${this.apiUrl}/${id}/regenerate`,
                {}
            )
        );

        return result;
    }

    // ======================
    // RETRY
    // ======================

    async retryReport(id: string): Promise<ApiResult<null>> {
        const result = await firstValueFrom(
            this.http.post<ApiResult<null>>(
                `${this.apiUrl}/${id}/retry`,
                {}
            )
        );

        return result;
    }
}